import { EIremboFormlyValidationTypes } from '@irembo-andela/irembogov3-common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AttachmentFieldFileFormat } from '../../../../../constants/attachment-field-file-format-emum-to-types.enum';
import {
  EAvailableConditionalValidations,
  IConditionalValidationConfig,
} from '../../../../../models/form-fields-formly-conditional-validation.model';
import {
  IFormFieldsValidationModel,
  IIdConditionsAgeLimit,
  IIdConditionsModel,
  IPatternValidation,
  IValidationLogic,
} from '../../../../../models/form-fields-formly-validation.model';
import {
  FieldValidationPatternConfigEnumToDescription,
  FieldValidationPatternConfigEnumToRegexString,
} from '../../../../field-validation-pattern-config-conversion.utils';
import { ValidationTypeDefaultMessage } from '../../../form-builder-validation-type-message.utils';
import {
  conditionalValidationConfigToExpression,
  setConditionalValidationExpressionOnField,
} from '../../../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';
import {
  clearFieldMessageByKey,
  updateFieldMessages,
  DEFAULT_PROPS_REQUIRED_EXPRESSION,
} from '../../../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { convertDaysObjectToArray } from '../../custom-datepicker-field-type.configuration';

interface IValueExpressionsAndMessages {
  value: unknown;
  expression: string;
  errorMessage?: string;
}

export class SettersConfigurationUtils {
  static setRequiredValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (
      model.validations?.[EIremboFormlyValidationTypes.required]?.logics
        ?.length === 0
    ) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.required);

    const requiredStatus: boolean =
      model.validations?.[EIremboFormlyValidationTypes.required]?.logics[0]?.[
        EIremboFormlyValidationTypes.required
      ] ?? false;

    if (field.props) {
      field.props.required = requiredStatus;
      field.props['defaultRequired'] = requiredStatus;
    } else {
      field.props = {
        required: requiredStatus,
        defaultRequired: requiredStatus,
      };
    }

    const propsRequiredExpression = DEFAULT_PROPS_REQUIRED_EXPRESSION;

    if (field.expressions) {
      field.expressions = {
        ...field.expressions,
        'props.required': propsRequiredExpression,
      };
    } else {
      field.expressions = {
        'props.required': propsRequiredExpression,
      };
    }

    const requiredMessage: string =
      model.validations?.[EIremboFormlyValidationTypes.required]?.logics[0]
        ?.errorMessage ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.required);

    if (requiredStatus) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.required,
        requiredMessage
      );
    }

    return field;
  }

  static setConditionalExpressionFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig,
    validationKey: string
  ): FormlyFieldConfig {
    if (!model?.validations || typeof model?.validations !== 'object') {
      delete field?.props?.['validationsModel'];
      return field;
    }

    new SettersConfigurationUtils().handleConditionalValidationTypes(
      validationKey,
      field,
      model
    );

    if (field?.props) {
      field.props['validationsModel'] = model.validations;
    } else {
      field.props = {
        validationsModel: model.validations,
      };
    }

    return field;
  }

  static setMinLengthValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (
      model.validations?.[EIremboFormlyValidationTypes.minLength]?.logics
        ?.length === 0
    ) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.minLength);

    const minLengthLogics: IValidationLogic[] | undefined =
      model.validations?.[EIremboFormlyValidationTypes.minLength]?.logics;
    const defaultLogic: IValidationLogic | undefined =
      minLengthLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || minLengthLogics?.[0];

    let minLength: number | undefined =
      defaultLogic?.['minLength'] ?? undefined;
    minLength = minLength === 0 ? undefined : minLength;

    if (field.props) {
      field.props.minLength = minLength;
    } else {
      field.props = {
        minLength,
      };
    }

    const minLengthMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minLength);

    if (minLength) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.minLength,
        minLengthMessage
      );
    }

    return field;
  }

  static setMaxLengthValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (
      model.validations?.[EIremboFormlyValidationTypes.maxLength]?.logics
        ?.length === 0
    ) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.maxLength);

    const maxLengthLogics: IValidationLogic[] | undefined =
      model.validations?.[EIremboFormlyValidationTypes.maxLength]?.logics;
    const defaultLogic: IValidationLogic | undefined =
      maxLengthLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || maxLengthLogics?.[0];

    let maxLength: number | undefined =
      defaultLogic?.['maxLength'] ?? undefined;
    maxLength = maxLength === 0 ? undefined : maxLength;

    if (field.props) {
      field.props.maxLength = maxLength;
    } else {
      field.props = {
        maxLength,
      };
    }

    const maxLengthMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maxLength);

    if (maxLength) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.maxLength,
        maxLengthMessage
      );
    }

    return field;
  }

  static setMinValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (
      model.validations?.[EIremboFormlyValidationTypes.min]?.logics?.length ===
      0
    ) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.min);

    const minLogics: IValidationLogic[] | undefined =
      model.validations?.[EIremboFormlyValidationTypes.min]?.logics;
    const defaultLogic: IValidationLogic | undefined =
      minLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || minLogics?.[0];

    let min: number | undefined = defaultLogic?.['min'] ?? undefined;
    min = min === 0 ? undefined : min;
    if (field.props) {
      field.props.min = min;
    } else {
      field.props = {
        min,
      };
    }

    const minMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.min);

    if (minMessage) {
      updateFieldMessages(field, EIremboFormlyValidationTypes.min, minMessage);
    }

    return field;
  }

  static setMaxValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (
      model.validations?.[EIremboFormlyValidationTypes.max]?.logics?.length ===
      0
    ) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.max);

    const maxLogics: IValidationLogic[] | undefined =
      model.validations?.[EIremboFormlyValidationTypes.max]?.logics;
    const defaultLogic: IValidationLogic | undefined =
      maxLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || maxLogics?.[0];

    let max: number | undefined = defaultLogic?.['max'] ?? undefined;
    max = max === 0 ? undefined : max;
    if (field.props) {
      field.props.max = max;
    } else {
      field.props = {
        max,
      };
    }

    const maxMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.max);

    if (maxMessage) {
      updateFieldMessages(field, EIremboFormlyValidationTypes.max, maxMessage);
    }

    return field;
  }

  static setPatternValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['patternValidation']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.pattern);

    const patternLogics: IValidationLogic[] | undefined =
      model.validations?.['patternValidation']?.logics;
    const defaultLogic: IValidationLogic | undefined =
      patternLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || patternLogics?.[0];

    const patternValidation: IPatternValidation | undefined =
      defaultLogic?.['patternValidation'];

    let regexPattern: string | undefined =
      FieldValidationPatternConfigEnumToRegexString(
        patternValidation?.patternType,
        patternValidation?.customPattern
      );
    let patternDescription: string | undefined =
      FieldValidationPatternConfigEnumToDescription(
        patternValidation?.patternType
      ) ?? `pattern match ${patternValidation?.customPattern}`;

    if (!patternValidation?.patternType) {
      regexPattern = undefined;
      patternDescription = undefined;
    }

    const patternMessage: string =
      defaultLogic?.errorMessage ??
      `${ValidationTypeDefaultMessage(
        EIremboFormlyValidationTypes.pattern
      )} : accepts ${patternDescription}`;

    if (regexPattern) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.pattern,
        patternMessage
      );
    }

    if (field.props) {
      field.props.pattern = regexPattern;
    } else {
      field.props = {
        pattern: regexPattern,
      };
    }

    return field;
  }

  static setIdConditionsValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['idConditions']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.minimumAge);
    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.maximumAge);

    const idConditionsLogics: IValidationLogic[] | undefined =
      model.validations?.['idConditions']?.logics;
    const defaultLogic: IValidationLogic | undefined =
      idConditionsLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || idConditionsLogics?.[0];
    const ageLimit: IIdConditionsAgeLimit | undefined =
      defaultLogic?.['idConditions']?.['ageLimit'];

    const minimumAgeLimit: number | undefined =
      ageLimit?.minimumAge ?? undefined;
    const maximumAgeLimit: number | undefined =
      ageLimit?.maximumAge ?? undefined;
    const dateFieldKey: string = ageLimit?.key ?? 'dateOfBirth';

    const ageLimitConfig: IIdConditionsAgeLimit = {
      key: dateFieldKey,
      minimumAge: minimumAgeLimit,
      maximumAge: maximumAgeLimit,
    };

    const fieldIdConditions: IIdConditionsModel = {
      ...(field?.props?.['idConditions'] ?? {}),
      ageLimit: ageLimitConfig,
    };

    if (field.props) {
      field.props['idConditions'] = fieldIdConditions;
    } else {
      field.props = {
        idConditions: fieldIdConditions,
      };
    }

    const minimumAgeMessage: string =
      defaultLogic?.errorMessages?.[EIremboFormlyValidationTypes.minimumAge] ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minimumAge);

    const maximumAgeMessage: string =
      defaultLogic?.errorMessages?.[EIremboFormlyValidationTypes.maximumAge] ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maximumAge);

    if (minimumAgeMessage) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.minimumAge,
        minimumAgeMessage
      );
    }
    if (maximumAgeMessage) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.maximumAge,
        maximumAgeMessage
      );
    }

    return field;
  }

  static setCommonValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig,
    validationKey: string
  ): FormlyFieldConfig {
    const invalidIdLogics: IValidationLogic[] | undefined =
      model.validations?.[validationKey]?.logics;
    const defaultLogic: IValidationLogic | undefined = invalidIdLogics?.[0];

    const invalidIdMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(
        validationKey as EIremboFormlyValidationTypes
      );

    if (invalidIdMessage) {
      updateFieldMessages(
        field,
        validationKey as EIremboFormlyValidationTypes,
        invalidIdMessage
      );
    }

    return field;
  }

  static setMinDateConfigValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['minDateConfig']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.minDate);

    if (!field.props) {
      field.props = {};
    }

    const minDateConfigLogics: IValidationLogic[] | undefined =
      model.validations?.['minDateConfig']?.logics;
    const defaultLogic: IValidationLogic | undefined = minDateConfigLogics?.[0];
    const minDateConfig: any = defaultLogic?.['minDateConfig'];

    if (minDateConfig) {
      if (
        minDateConfig.minDateType === 'relative' &&
        minDateConfig.relativeMinDateValue &&
        minDateConfig.relativeMinDateUnit
      ) {
        const value = minDateConfig.relativeMinDateValue;
        const unit = minDateConfig.relativeMinDateUnit;

        field.props['addRemoveTimeMinDate'] = `${value}:${unit}`;
      } else {
        field.props['addRemoveTimeMinDate'] = undefined;
      }
    }

    const minDateMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minDate);

    if (minDateMessage) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.minDate,
        minDateMessage
      );
    }

    return field;
  }

  static setMaxDateConfigValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['maxDateConfig']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.maxDate);

    if (!field.props) {
      field.props = {};
    }

    const maxDateConfigLogics: IValidationLogic[] | undefined =
      model.validations?.['maxDateConfig']?.logics;
    const defaultLogic: IValidationLogic | undefined = maxDateConfigLogics?.[0];
    const maxDateConfig: any = defaultLogic?.['maxDateConfig'];

    if (maxDateConfig) {
      if (
        maxDateConfig.maxDateType === 'relative' &&
        maxDateConfig.relativeMaxDateValue &&
        maxDateConfig.relativeMaxDateUnit
      ) {
        const value = maxDateConfig.relativeMaxDateValue;
        const unit = maxDateConfig.relativeMaxDateUnit;

        field.props['addRemoveTimeMaxDate'] = `${value}:${unit}`;
      } else {
        field.props['addRemoveTimeMaxDate'] = undefined;
      }
    }

    const maxDateMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maxDate);

    if (maxDateMessage) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.maxDate,
        maxDateMessage
      );
    }

    return field;
  }

  static setAllowedDaysOfTheWeekValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['allowedDaysOfTheWeek']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(
      field,
      EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
    );

    if (!field.props) {
      field.props = {};
    }

    const allowedDaysOfTheWeekLogics: IValidationLogic[] | undefined =
      model.validations?.['allowedDaysOfTheWeek']?.logics;
    const defaultLogic: IValidationLogic | undefined =
      allowedDaysOfTheWeekLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || allowedDaysOfTheWeekLogics?.[0];
    const allowedDaysOfTheWeek: Record<string, boolean> | undefined =
      defaultLogic?.['allowedDaysOfTheWeek'];

    if (allowedDaysOfTheWeek) {
      const daysAllowed = convertDaysObjectToArray(allowedDaysOfTheWeek);

      if (daysAllowed.length) {
        field.props['allowedDaysOfTheWeek'] = daysAllowed;
      } else {
        delete field.props['allowedDaysOfTheWeek'];
      }
    }

    const allowedDaysOfTheWeekMessage: string =
      defaultLogic?.errorMessage ??
      ValidationTypeDefaultMessage(
        EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
      );

    if (allowedDaysOfTheWeekMessage) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed,
        allowedDaysOfTheWeekMessage
      );
    }

    return field;
  }

  static setFileSizeValidatorFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['fileSize']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(
      field,
      EIremboFormlyValidationTypes.minimumUploadSize
    );
    clearFieldMessageByKey(
      field,
      EIremboFormlyValidationTypes.maximumUploadSize
    );

    const fileSizeLogics: IValidationLogic[] | undefined =
      model.validations?.['fileSize']?.logics;
    const defaultLogic: IValidationLogic | undefined = fileSizeLogics?.[0];

    const minimumUploadSize: number | undefined =
      defaultLogic?.['fileSize']?.minimumUploadSize ?? undefined;
    const maximumUploadSize: number | undefined =
      defaultLogic?.['fileSize']?.maximumUploadSize ?? undefined;

    if (field.props) {
      field.props['minimumUploadSize'] = minimumUploadSize;
      field.props['maximumUploadSize'] = maximumUploadSize;
    } else {
      field.props = {
        minimumUploadSize: minimumUploadSize,
        maximumUploadSize: maximumUploadSize,
      };
    }

    const minFileSizeMessage: string =
      defaultLogic?.errorMessages?.['minimumUploadSizeMessage'] ??
      ValidationTypeDefaultMessage(
        EIremboFormlyValidationTypes.minimumUploadSize
      );

    const maxFileSizeMessage: string =
      defaultLogic?.errorMessages?.['maximumUploadSizeMessage'] ??
      ValidationTypeDefaultMessage(
        EIremboFormlyValidationTypes.maximumUploadSize
      );

    if (minFileSizeMessage) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.minimumUploadSize,
        minFileSizeMessage
      );
    }
    if (maxFileSizeMessage) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.maximumUploadSize,
        maxFileSizeMessage
      );
    }

    return field;
  }

  static setAllowedFileFormatsValidatorFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['fileFormats']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(
      field,
      EIremboFormlyValidationTypes.invalidfileformat
    );

    const fileFormatsLogics: IValidationLogic[] | undefined =
      model.validations?.['fileFormats']?.logics;
    const defaultLogic: IValidationLogic | undefined = fileFormatsLogics?.[0];
    let allowedFormats: string[] | undefined = [];

    Object.keys(AttachmentFieldFileFormat).forEach(key => {
      if (defaultLogic?.['fileFormats']?.[key]) {
        allowedFormats = [
          ...AttachmentFieldFileFormat[key].value,
          ...(allowedFormats ?? []),
        ];
      }
    });

    if (field.props) {
      field.props['allowedFormats'] = allowedFormats;
    } else {
      field.props = {
        allowedFormats,
      };
    }

    if (allowedFormats.length > 0) {
      const allowedFormatsMessage: string =
        defaultLogic?.errorMessage ??
        ValidationTypeDefaultMessage(
          EIremboFormlyValidationTypes.invalidfileformat
        );

      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.invalidfileformat,
        allowedFormatsMessage
      );
    }

    return field;
  }

  static setTableSelectionValidatorUpdateFieldFromModel(
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ): FormlyFieldConfig {
    if (model.validations?.['tableValidation']?.logics?.length === 0) {
      return field;
    }

    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.minSelection);
    clearFieldMessageByKey(field, EIremboFormlyValidationTypes.maxSelection);

    const tableValidationLogics: IValidationLogic[] | undefined =
      model.validations?.['tableValidation']?.logics;
    const defaultLogic: IValidationLogic | undefined =
      tableValidationLogics?.find(
        logic => !logic?.conditions || logic?.conditions?.length === 0
      ) || tableValidationLogics?.[0];

    let minSelection: number | undefined =
      defaultLogic?.['tableValidation']?.minSelection ?? undefined;
    minSelection = minSelection === 0 ? undefined : minSelection;
    let maxSelection: number | undefined =
      defaultLogic?.['tableValidation']?.maxSelection ?? undefined;
    maxSelection = maxSelection === 0 ? undefined : maxSelection;

    if (field.props) {
      field.props['minSelection'] = minSelection;
      field.props['maxSelection'] = maxSelection;
    } else {
      field.props = {
        minSelection: minSelection,
        maxSelection: maxSelection,
      };
    }

    const minSelectionMessage: string =
      defaultLogic?.errorMessages?.['minSelectionMessage'] ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minSelection);

    const maxSelectionMessage: string =
      defaultLogic?.errorMessages?.['maxSelectionMessage'] ??
      ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maxSelection);

    if (minSelection) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.minSelection,
        minSelectionMessage
      );
    }
    if (maxSelection) {
      updateFieldMessages(
        field,
        EIremboFormlyValidationTypes.maxSelection,
        maxSelectionMessage
      );
    }

    return field;
  }

  private handleConditionalValidationTypes(
    validationType: string,
    field: FormlyFieldConfig,
    model: IFormFieldsValidationModel
  ): FormlyFieldConfig {
    const configuredFieldRootModelPath: string | null | undefined =
      model.configuredFieldRootModelPath;
    const validations: IValidationLogic[] | undefined =
      model.validations?.[validationType]?.logics;

    if (
      validations == undefined ||
      validations.length === 0 ||
      !validations.some(v => v?.conditions && v?.conditions?.length > 0)
    ) {
      if (field?.expressions) {
        let expressionKey = `props.${validationType}`;
        let validationMessageExpressionKey = `validation.messages['${validationType}']`;

        if (
          validationType === EAvailableConditionalValidations.patternValidation
        ) {
          expressionKey = 'props.pattern';
          validationMessageExpressionKey = `validation.messages['pattern']`;
        }

        delete field.expressions[expressionKey];
        delete field.expressions[validationMessageExpressionKey];
      }

      return field;
    }

    switch (validationType) {
      case EAvailableConditionalValidations.required: {
        let queryExpression = conditionalValidationConfigToExpression(
          {
            conditions: validations[0]?.conditions,
            validation: validations[0]?.[validationType],
          },
          configuredFieldRootModelPath
        );

        if (validations[0]?.[validationType] === false) {
          queryExpression = `!(${queryExpression})`;
        }

        return setConditionalValidationExpressionOnField(
          queryExpression,
          field,
          'props.required'
        );
      }
      case EAvailableConditionalValidations.minLength:
      case EAvailableConditionalValidations.maxLength:
      case EAvailableConditionalValidations.patternValidation: {
        let configs: IConditionalValidationConfig[] | undefined = undefined;
        let expressionKey = `props.${validationType}`;
        let validationMessageExpressionKey = `validation.messages['${validationType}']`;
        if (validationType === EAvailableConditionalValidations.minLength) {
          configs = [];

          validations.forEach((validation: IValidationLogic) => {
            configs?.push({
              conditions: validation?.conditions,
              validation: validation?.[validationType],
              errorMessage: validation?.errorMessage,
            });
          });
        }
        if (validationType === EAvailableConditionalValidations.maxLength) {
          configs = [];

          validations.forEach((validation: IValidationLogic) => {
            configs?.push({
              conditions: validation?.conditions,
              validation: validation?.[validationType],
              errorMessage: validation?.errorMessage,
            });
          });
        }
        if (
          validationType === EAvailableConditionalValidations.patternValidation
        ) {
          expressionKey = 'props.pattern';
          validationMessageExpressionKey = `validation.messages['pattern']`;
          configs = [];

          validations.forEach((validation: IValidationLogic) => {
            configs?.push({
              conditions: validation?.conditions,
              validation: validation?.[validationType],
              errorMessage: validation?.errorMessage,
            });
          });
        }
        if (!configs?.[0]) {
          // No conditions set for validation
          return field;
        }

        const conditonExpressions: IValueExpressionsAndMessages[] = [];

        configs?.forEach((validationConfig: IConditionalValidationConfig) => {
          if (!validationConfig?.conditions) {
            return;
          }

          let validationValue = validationConfig?.validation;

          if (
            validationType ===
            EAvailableConditionalValidations.patternValidation
          ) {
            const regExPattern = FieldValidationPatternConfigEnumToRegexString(
              (validationConfig?.validation as IPatternValidation)?.patternType,
              (validationConfig?.validation as IPatternValidation)
                ?.customPattern
            );
            validationValue = `/${regExPattern}/`;
          }

          conditonExpressions.push({
            value: validationValue,
            expression: conditionalValidationConfigToExpression(
              validationConfig,
              configuredFieldRootModelPath
            ),
            errorMessage: validationConfig.errorMessage
              ? `formState.getTranslation('${validationConfig.errorMessage}')`
              : undefined,
          });
        });

        const defaultValidation =
          validations.find(
            logic => !logic?.conditions || logic?.conditions?.length === 0
          ) || validations[0];

        let defaultExpressionValue: unknown = null;

        if (validations.length > 1) {
          if (
            validationType ===
            EAvailableConditionalValidations.patternValidation
          ) {
            const regExPattern = FieldValidationPatternConfigEnumToRegexString(
              (defaultValidation?.[validationType] as IPatternValidation)
                ?.patternType,
              (defaultValidation?.[validationType] as IPatternValidation)
                ?.customPattern
            );
            defaultExpressionValue = regExPattern;
          } else {
            defaultExpressionValue = defaultValidation?.[validationType];
          }
        }

        const defaultErrorMessage =
          defaultValidation?.errorMessage ?? 'Field is invalid.';
        let finalExpression = defaultExpressionValue as unknown as string;
        let validationMessageExpression = `formState.getTranslation('${defaultErrorMessage}')`;

        conditonExpressions
          .reverse()
          .forEach((item: IValueExpressionsAndMessages) => {
            finalExpression = `${item.expression} ? ${item.value} : ${finalExpression}`;

            if (item.errorMessage) {
              validationMessageExpression = `${item.expression} ? ${item.errorMessage} : ${validationMessageExpression}`;
            }
          });

        setConditionalValidationExpressionOnField(
          finalExpression,
          field,
          expressionKey
        );
        setConditionalValidationExpressionOnField(
          validationMessageExpression,
          field,
          validationMessageExpressionKey
        );
      }
    }

    return field;
  }
}
